import { OrganizationsResponse } from '../../services/RepositoryService';
import { Repository } from '../../types';
import { OrganizationItem } from './types';

export interface FilterOptions {
  selectedOrganization: string;
  selectedVisibility: string;
  selectedLanguage: string;
  selectedScanStatus: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

export const buildAvailableOrganizations = (
  orgsData: OrganizationsResponse | null,
  repositories: Repository[],
): OrganizationItem[] => {
  if (orgsData) {
    const items: OrganizationItem[] = [];

    if (orgsData.personalAccount.login) {
      items.push({
        login: orgsData.personalAccount.login,
        accessStatus: 'personal',
        repoCount: orgsData.personalAccount.repoCount,
      });
    }

    const active = orgsData.organizations.filter((o) => o.accessStatus === 'active');
    const restricted = orgsData.organizations.filter((o) => o.accessStatus === 'restricted');
    const blocked = orgsData.organizations.filter((o) => o.accessStatus === 'blocked');

    for (const org of [...active, ...restricted, ...blocked]) {
      items.push({
        login: org.login,
        accessStatus: org.accessStatus,
        repoCount: org.repoCount,
      });
    }

    const knownLogins = new Set(items.map((i) => i.login));
    for (const repo of repositories) {
      const owner = repo.fullName.split('/')[0];
      if (owner && !knownLogins.has(owner)) {
        knownLogins.add(owner);
        items.push({
          login: owner,
          accessStatus: 'active',
          repoCount: repositories.filter((r) => r.fullName.startsWith(owner + '/')).length,
        });
      }
    }

    return items;
  }

  const orgs = repositories
    .map((repo) => repo.fullName.split('/')[0])
    .filter((org): org is string => Boolean(org))
    .filter((org, index, arr) => arr.indexOf(org) === index)
    .sort();
  return orgs.map((login) => ({ login, accessStatus: 'active' as const, repoCount: 0 }));
};

export const filterAndSortRepositories = (
  repositories: Repository[],
  options: FilterOptions,
): Repository[] => {
  let filtered = [...repositories];

  if (options.selectedOrganization) {
    filtered = filtered.filter(
      (repo) => repo.fullName.split('/')[0] === options.selectedOrganization,
    );
  }

  if (options.selectedVisibility === 'public') {
    filtered = filtered.filter((repo) => !repo.private);
  } else if (options.selectedVisibility === 'private') {
    filtered = filtered.filter((repo) => repo.private);
  }

  if (options.selectedLanguage) {
    filtered = filtered.filter(
      (repo) => (repo.language || '').toLowerCase() === options.selectedLanguage.toLowerCase(),
    );
  }

  if (options.selectedScanStatus) {
    filtered = filtered.filter((repo) => repo.scanStatus === options.selectedScanStatus);
  }

  filtered.sort((a, b) => {
    let cmp = 0;
    switch (options.sortBy) {
      case 'name':
        cmp = (a.fullName || a.name).localeCompare(b.fullName || b.name);
        break;
      case 'date':
        cmp = new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
        break;
      case 'stars':
        cmp = (a.metadata?.stars ?? a.stars ?? 0) - (b.metadata?.stars ?? b.stars ?? 0);
        break;
    }
    return options.sortOrder === 'asc' ? cmp : -cmp;
  });

  return filtered;
};

export const collectAvailableLanguages = (repositories: Repository[]): string[] => {
  const langs = repositories.map((r) => r.language).filter((l): l is string => Boolean(l));
  return [...new Set(langs)].sort();
};
